import React, { useCallback, useEffect, useMemo } from 'react';
import { Flex } from 'onefaceui-react-components';
import { useDispatch, useSelector } from 'react-redux';
import { actionCreators as actionsModals } from 'react/api/actions/modals';
import { getResourceTo } from 'react/utils/utils';
import { useStopDetails } from 'react/queries/StopDetails';
import Modal from 'react/components/Modal';
import Loading from 'react/components/Loading';

import { Container } from './styles';
import RouteStatus from './RouteStatus';
import StopDetailModalFooter from './StopDetailModalFooter';
import LocationInfoDisplay, { getAddress, getPhones } from './LocationInfoDisplay';
import SizeInfoDisplay from './SizeInfoDisplay';
import StopStatusDisplay from './StopStatusDisplay';
import { getStopStatus } from 'react/utils/stopUtils';
import { useSizeConfigs, useModalContainer } from './hooks';

const MODAL_KEY = 'shortStopDetails';

export default function ShortStopDetailsModal() {
  const dispatch = useDispatch();

  const {
    title,
    isOpen,
    data: modalData
  } = useSelector((state) => state.modals.get(MODAL_KEY).toJS());

  const {
    data: stopDetail,
    refetch,
    isFetching
  } = useStopDetails(modalData?.id);

  const closeModal = useCallback(() => {
    dispatch(
      actionsModals.closeModal({
        modalKey: MODAL_KEY
      })
    );
  }, [dispatch]);

  const { location } = stopDetail || {};
  const stopStatus = useMemo(() => getStopStatus(stopDetail), [stopDetail]);

  const getContainer = useModalContainer();
  const sizeAliasConfigs = useSizeConfigs(stopDetail);

  const address = useMemo(() => getAddress(location), [location]);
  const phones = useMemo(() => getPhones(location), [location]);

  useEffect(() => {
    if (modalData?.id) refetch();
  }, [modalData?.id, refetch]);

  return (
    <Modal
      isOpen={isOpen}
      title={getResourceTo(title)}
      crossAxisBtn={false}
      closeModalFn={closeModal}
      parentSelector={getContainer}
      footer={<StopDetailModalFooter stopDetail={stopDetail} stopStatus={stopStatus} closeModal={closeModal} />}
    >
      <Container data-testid="shortStopDetails-container">
        <Flex
          alignItems="center"
          justifyContent="center"
          className="loading-content"
          shouldShow={isFetching}
        >
          <Loading />
        </Flex>

        <Flex shouldShow={!isFetching} flexDirection="column" gap={4}>
          <LocationInfoDisplay
            location={location}
            address={address}
            phones={phones}
          />

          <StopStatusDisplay
            stopStatus={stopStatus}
            stopDetail={stopDetail}
          />

          <SizeInfoDisplay sizeAliasConfigs={sizeAliasConfigs} />

          {stopDetail?.route && <RouteStatus route={stopDetail.route} />}
        </Flex>
      </Container>
    </Modal>
  );
}
